import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import { jobCardsAPI, operatorActivitiesAPI, supervisorNotesAPI, usersAPI, bomAPI, estimatesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import appLogo from '../assets/logo.png';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import PerformanceReports from './PerformanceReports';
import DateFilter from './DateFilter';
import OperatorEditHistory from './OperatorEditHistory';
import PressingBalance from './PressingBalance';
import WorkLogHistory from './WorkLogHistory';
import { BillView, BillDialog } from './BillView';
import FloorStatus from './FloorStatus';
import { pressingProgress } from '../utils/pressing';
import {
  LogOut,
  FileText,
  Users,
  MessageSquare,
  Plus,
  Activity,
  Layers,
  Clock,
  Package,
  Upload,
  Image,
  Camera,
  CheckCircle,
  Check,
  Receipt,
  Eye,
  X,
  Download,
  FileSpreadsheet,
  BarChart3,
  RotateCcw,
  Printer,
  FolderKanban,
  AlertCircle,
  Filter,
  Search
} from 'lucide-react';

import NotificationBell from './NotificationBell';
import { printJobCard } from '../utils/printUtils';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const SupervisorDashboard = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('jobcards');
  const [jobCards, setJobCards] = useState([]);
  const [activities, setActivities] = useState([]);
  const [operators, setOperators] = useState([]);
  const [receivedBOMs, setReceivedBOMs] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Note dialog state
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteData, setNoteData] = useState({ job_card_id: '', operator_id: '', note: '' });
  const [notes, setNotes] = useState([]);
  
  // Invoice upload state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedBOM, setSelectedBOM] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [viewingPhotoUrl, setViewingPhotoUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Job card detail dialog state
  const [viewJobCard, setViewJobCard] = useState(null);
  const [viewingPressingImage, setViewingPressingImage] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectingJobCardId, setRejectingJobCardId] = useState(null);
  // Back-to-production dialog (card awaiting review returns to the floor)
  const [reopenJobCard, setReopenJobCard] = useState(null);
  // One-page bill shown before approval (card awaiting review)
  const [billJobCard, setBillJobCard] = useState(null);
  const [reopenReason, setReopenReason] = useState('');
  const [reopening, setReopening] = useState(false);

  // Stats dialog state
  const [showStatsDialog, setShowStatsDialog] = useState(false);
  const [statsDialogType, setStatsDialogType] = useState(null); // 'active', 'pending_review', 'completed'
  const [breakdownLine, setBreakdownLine] = useState(null);

  // Date filter state
  const [dateFilter, setDateFilter] = useState('all');
  const [dateFilterStart, setDateFilterStart] = useState(null);
  const [dateFilterEnd, setDateFilterEnd] = useState(null);
  const [coordinatorFilter, setCoordinatorFilter] = useState('all');
  const [clientSearch, setClientSearch] = useState('');

  const handleApproveJobCard = async (jobCardId) => {
    try {
      await jobCardsAPI.supervisorApprove(jobCardId);
      toast({ title: "Success", description: "Job card approved and sent to accounts" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to approve", variant: "destructive" });
    }
  };

  const handleRejectJobCard = async () => {
    if (!rejectReason.trim()) {
      toast({ title: "Error", description: "Please provide a rejection reason", variant: "destructive" });
      return;
    }
    try {
      await jobCardsAPI.supervisorReject(rejectingJobCardId, rejectReason);
      toast({ title: "Success", description: "Job card rejected and sent back to coordinator" });
      setShowRejectDialog(false);
      setRejectReason('');
      setRejectingJobCardId(null);
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to reject", variant: "destructive" });
    }
  };

  const closeReopenDialog = () => { setReopenJobCard(null); setReopenReason(''); };

  const handleReopenForProduction = async () => {
    if (!reopenReason.trim()) {
      toast({ title: "Reason needed", description: "Say why the card is going back to production", variant: "destructive" });
      return;
    }
    setReopening(true);
    try {
      await jobCardsAPI.reopenForProduction(reopenJobCard.id, reopenReason.trim());
      toast({ title: "Back in production", description: `JC ${reopenJobCard.job_card_number} is on the operators' list again` });
      closeReopenDialog();
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to send back to production", variant: "destructive" });
    } finally {
      setReopening(false);
    }
  };

  const handleExportPDF = async (jc) => {
    try {
      const blob = await jobCardsAPI.exportPDF(jc.id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${jc.job_card_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast({ title: "Success", description: "PDF downloaded" });
    } catch {
      toast({ title: "Error", description: "Failed to export PDF", variant: "destructive" });
    }
  };

  const handleExportExcel = async (jc) => {
    try {
      const blob = await jobCardsAPI.exportExcel(jc.id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${jc.job_card_number}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast({ title: "Success", description: "Excel downloaded" });
    } catch {
      toast({ title: "Error", description: "Failed to export Excel", variant: "destructive" });
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [jobCardsData, activitiesData, operatorsData, bomsData, estimatesData] = await Promise.all([
        jobCardsAPI.getAll(),
        operatorActivitiesAPI.getAll({ today_only: true }),
        usersAPI.getOperators(),
        bomAPI.getReceived().catch(() => []),
        estimatesAPI.getAll().catch(() => [])
      ]);
      setJobCards(jobCardsData);
      setActivities(activitiesData);
      setOperators(operatorsData);
      setReceivedBOMs(bomsData);
      setEstimates(estimatesData);
    } catch (error) {
      toast({ title: "Error", description: "Failed to fetch data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleUploadInvoice = async (file) => {
    if (!selectedBOM || !file) return;
    setUploading(true);
    try {
      await bomAPI.uploadInvoice(selectedBOM.id, file);
      toast({ title: "Success", description: "Invoice photo uploaded successfully" });
      setPreviewUrl(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to upload invoice", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteInvoicePhoto = async (bomId, imageUrl) => {
    try {
      await bomAPI.deleteInvoicePhoto(bomId, imageUrl);
      toast({ title: "Success", description: "Invoice photo deleted" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete photo", variant: "destructive" });
    }
  };

  const openUploadDialog = (bom) => {
    setSelectedBOM(bom);
    setPreviewUrl(null);
    setShowUploadDialog(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const viewInvoicePhoto = (photoUrl) => {
    setViewingPhotoUrl(photoUrl);
    setShowPhotoDialog(true);
  };

  const handleCreateNote = async () => {
    try {
      await supervisorNotesAPI.create(noteData);
      toast({ title: "Success", description: "Note added successfully" });
      setShowNoteDialog(false);
      setNoteData({ job_card_id: '', operator_id: '', note: '' });
    } catch (error) {
      toast({ title: "Error", description: "Failed to add note", variant: "destructive" });
    }
  };

  const fetchNotesForJobCard = async (jobCardId) => {
    try {
      const notesData = await supervisorNotesAPI.getAll({ job_card_id: jobCardId });
      setNotes(notesData);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-amber-100 text-amber-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Stats helpers
  const isDelayed = (jc) => {
    if (!jc.pushed_to_production_at) return false;
    if (['closed', 'completed', 'sent_to_accounts', 'pending_supervisor_review', 'cancelled'].includes(jc.status)) return false;
    const pushedDate = new Date(jc.pushed_to_production_at);
    const threeWeeksMs = 21 * 24 * 60 * 60 * 1000;
    return (Date.now() - pushedDate.getTime()) > threeWeeksMs;
  };

  const filterByDate = (createdAt) => {
    if (!createdAt) return true;
    if (dateFilter === 'all') return true;
    
    const itemDate = new Date(createdAt);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    switch (dateFilter) {
      case 'today':
        return itemDate >= today;
      case 'yesterday':
        const todayStart = new Date(today);
        return itemDate >= yesterday && itemDate < todayStart;
      case 'weekly':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return itemDate >= weekAgo;
      case 'monthly':
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        return itemDate >= monthAgo;
      case 'custom':
        if (dateFilterStart && dateFilterEnd) {
          const start = new Date(dateFilterStart);
          start.setHours(0, 0, 0, 0);
          const end = new Date(dateFilterEnd);
          end.setHours(23, 59, 59, 999);
          return itemDate >= start && itemDate <= end;
        }
        return true;
      default:
        return true;
    }
  };

  // Coordinators present in the loaded job cards (for the filter dropdown)
  const coordinatorOptions = [...new Set(jobCards.map(jc => jc.created_by_name).filter(Boolean))].sort();

  const filteredJobCards = jobCards.filter(jc =>
    filterByDate(jc.created_at) &&
    (coordinatorFilter === 'all' || jc.created_by_name === coordinatorFilter) &&
    (!clientSearch.trim() ||
      (jc.customer_name || '').toLowerCase().includes(clientSearch.trim().toLowerCase()) ||
      (jc.reference || '').toLowerCase().includes(clientSearch.trim().toLowerCase()))
  );
  const activeJobCards = filteredJobCards.filter(jc => !['completed', 'invoiced', 'closed', 'sent_to_accounts', 'cancelled'].includes(jc.status));
  const pendingReviewCards = filteredJobCards.filter(jc => jc.status === 'pending_supervisor_review');
  const completedJobCards = filteredJobCards.filter(jc => ['completed', 'invoiced', 'sent_to_accounts'].includes(jc.status));

  const getStatsJobCards = () => {
    if (!statsDialogType) return [];
    switch (statsDialogType) {
      case 'active': return activeJobCards;
      case 'pending_review': return pendingReviewCards;
      case 'completed': return completedJobCards;
      default: return [];
    }
  };

  const getStatsDialogTitle = () => {
    switch (statsDialogType) {
      case 'active': return 'Active Job Cards';
      case 'pending_review': return 'Pending Review';
      case 'completed': return 'Completed Job Cards';
      default: return 'Job Cards';
    }
  };

  const getStatsDialogColor = () => {
    switch (statsDialogType) {
      case 'active': return 'text-blue-600';
      case 'pending_review': return 'text-orange-600';
      case 'completed': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const handleStatCardClick = (type) => {
    setStatsDialogType(type);
    setShowStatsDialog(true);
  };

  const handleStatsJobCardClick = (jc) => {
    setShowStatsDialog(false);
    setViewJobCard(jc);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden">
              <img src={appLogo} alt="Interior Job Work" className="w-[60px] h-[60px] object-cover" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Interior Job Work</h1>
              <p className="text-xs text-gray-500">Supervisor Panel</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600 hidden sm:block">{user?.name}</span>
            <NotificationBell />
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-5 lg:w-[650px]">
            <TabsTrigger value="jobcards" className="flex items-center gap-2" data-testid="supervisor-jobcards-tab">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Job Cards</span>
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2" data-testid="supervisor-invoices-tab">
              <Receipt className="w-4 h-4" />
              <span className="hidden sm:inline">Invoices</span>
              {receivedBOMs.filter(b => !b.invoice_photo_url).length > 0 && (
                <span className="ml-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
                  {receivedBOMs.filter(b => !b.invoice_photo_url).length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-2" data-testid="supervisor-reports-tab">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Reports</span>
            </TabsTrigger>
            <TabsTrigger value="operators" className="flex items-center gap-2" data-testid="supervisor-operators-tab">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Operators</span>
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex items-center gap-2" data-testid="supervisor-activity-tab">
              <Activity className="w-4 h-4" />
              <span className="hidden sm:inline">Activity</span>
            </TabsTrigger>
          </TabsList>

          {/* Job Cards Tab */}
          <TabsContent value="jobcards" className="space-y-4">
            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card 
                className="cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all bg-gradient-to-br from-blue-500 to-blue-600 text-white"
                onClick={() => handleStatCardClick('active')}
                data-testid="sup-stat-card-active"
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-100">Active</p>
                      <p className="text-2xl font-bold">{activeJobCards.length}</p>
                    </div>
                    <FolderKanban className="w-8 h-8 text-blue-200" />
                  </div>
                </CardContent>
              </Card>
              <Card 
                className="cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all bg-gradient-to-br from-orange-500 to-orange-600 text-white"
                onClick={() => handleStatCardClick('pending_review')}
                data-testid="sup-stat-card-pending"
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-orange-100">Pending Review</p>
                      <p className="text-2xl font-bold">{pendingReviewCards.length}</p>
                    </div>
                    <AlertCircle className="w-8 h-8 text-orange-200" />
                  </div>
                </CardContent>
              </Card>
              <Card 
                className="cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all bg-gradient-to-br from-green-500 to-green-600 text-white"
                onClick={() => handleStatCardClick('completed')}
                data-testid="sup-stat-card-completed"
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-100">Completed</p>
                      <p className="text-2xl font-bold">{completedJobCards.length}</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* What's pending at each stage (Manikandan's suggestion) */}
            <FloorStatus jobCards={jobCards} onOpenCard={(jc) => setViewJobCard(jc)} />

            {/* Date Filter */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Filter className="w-4 h-4" />
                <span>Filter:</span>
              </div>
              <DateFilter
                value={dateFilter}
                onChange={setDateFilter}
                startDate={dateFilterStart}
                endDate={dateFilterEnd}
                onDateRangeChange={(start, end) => {
                  setDateFilterStart(start);
                  setDateFilterEnd(end);
                }}
              />
              <Select value={coordinatorFilter} onValueChange={setCoordinatorFilter}>
                <SelectTrigger className="w-[190px] h-9" data-testid="coordinator-filter">
                  <SelectValue placeholder="All Coordinators" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Coordinators</SelectItem>
                  {coordinatorOptions.map(name => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search client or reference..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="pl-9 h-9 w-[200px]"
                  data-testid="client-search"
                />
              </div>
              {(dateFilter !== 'all' || coordinatorFilter !== 'all' || clientSearch.trim()) && (
                <Button variant="ghost" size="sm" onClick={() => { setDateFilter('all'); setDateFilterStart(null); setDateFilterEnd(null); setCoordinatorFilter('all'); setClientSearch(''); }}>
                  Clear Filter
                </Button>
              )}
              <span className="text-sm text-gray-500 ml-auto">
                {filteredJobCards.length} job card{filteredJobCards.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Open Job Cards</h2>
              <Button size="sm" onClick={() => setShowNoteDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Note
              </Button>
            </div>

            <div className="grid gap-4">
              {filteredJobCards.map(jc => (
                <Card key={jc.id} className={jc.status === 'in_production' ? 'ring-2 ring-emerald-400 ring-offset-1' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-blue-700 cursor-pointer hover:underline" onClick={() => setViewJobCard(jc)}>{jc.job_card_number}</h3>
                          <Badge className={
                            jc.status === 'in_production' ? 'bg-emerald-500 text-white animate-pulse' :
                            jc.status === 'open' ? 'bg-blue-100 text-blue-800' :
                            jc.status === 'in_progress' ? 'bg-amber-100 text-amber-800' :
                            jc.status === 'sent_to_accounts' ? 'bg-teal-100 text-teal-800' :
                            jc.status === 'completed' ? 'bg-green-100 text-green-800' :
                            jc.status === 'pending_supervisor_review' ? 'bg-orange-500 text-white animate-pulse' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {jc.status === 'in_production' ? 'LIVE' : jc.status?.replace(/_/g, ' ')}
                          </Badge>
                          {(() => {
                            if (!jc.pushed_to_production_at) return null;
                            if (['closed', 'completed', 'sent_to_accounts'].includes(jc.status)) return null;
                            const pushedDate = new Date(jc.pushed_to_production_at);
                            const threeWeeksMs = 21 * 24 * 60 * 60 * 1000;
                            if ((Date.now() - pushedDate.getTime()) > threeWeeksMs) {
                              return <Badge className="bg-red-100 text-red-700 border border-red-300 animate-pulse" data-testid={`delayed-badge-${jc.job_card_number}`}>Delayed</Badge>;
                            }
                            return null;
                          })()}
                        </div>
                        <p className="text-sm text-gray-600">{jc.customer_name}</p>
                        {jc.created_by_name && (
                          <p className="text-xs text-blue-600">Coordinator: {jc.created_by_name}</p>
                        )}
                        {jc.reference && <p className="text-xs text-gray-500">Ref: {jc.reference}</p>}
                        <div className="flex gap-4 mt-2 text-sm">
                          <span>Cut: <strong>{jc.cutting_qty_total || 0}</strong></span>
                          <PressingBalance jobCard={jc} variant="inline" />
                          <span>Bore: <strong>{jc.boring_qty_total || 0}</strong></span>
                          {jc.created_at && (
                            <span className="text-gray-500 text-xs flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(jc.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                          )}
                        </div>
                        
                        {/* Show cutting status */}
                        {jc.card_type === 'cutting' && (
                          <div className="mt-2">
                            <span className="text-xs text-gray-500">Cutting Status: </span>
                            <Badge className={
                              jc.cutting_status === 'completed' ? 'bg-green-100 text-green-800' :
                              jc.cutting_status === 'started' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {jc.cutting_status?.replace(/_/g, ' ') || 'Not Started'}
                            </Badge>
                          </div>
                        )}
                        
                        {/* Show pressing line progress */}
                        {(jc.normal_pressing_lines?.length > 0 || jc.hot_pressing_lines?.length > 0 || jc.veneer_pressing_lines?.length > 0) && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {jc.normal_pressing_lines?.length > 0 && (() => {
                              const done = jc.normal_pressing_lines.filter(l => l.status === 'completed').length;
                              const total = jc.normal_pressing_lines.length;
                              return (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${done === total ? 'bg-green-100 text-green-700' : done > 0 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>
                                  Normal: {done}/{total} done
                                </span>
                              );
                            })()}
                            {jc.hot_pressing_lines?.length > 0 && (() => {
                              const done = jc.hot_pressing_lines.filter(l => l.status === 'completed').length;
                              const total = jc.hot_pressing_lines.length;
                              return (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${done === total ? 'bg-green-100 text-green-700' : done > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                                  Hot: {done}/{total} done
                                </span>
                              );
                            })()}
                            {jc.veneer_pressing_lines?.length > 0 && (() => {
                              const done = jc.veneer_pressing_lines.filter(l => l.status === 'completed').length;
                              const total = jc.veneer_pressing_lines.length;
                              return (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${done === total ? 'bg-green-100 text-green-700' : done > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                                  Veneer: {done}/{total} done
                                </span>
                              );
                            })()}
                            {jc.boring_status && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${jc.boring_status === 'completed' ? 'bg-green-100 text-green-700' : jc.boring_status === 'started' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                                Boring: {jc.boring_status.replace(/_/g, ' ')}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 flex-shrink-0 flex-wrap">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setViewJobCard(jc)}
                          data-testid={`view-jc-${jc.job_card_number}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleExportPDF(jc)} title="Export PDF">
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleExportExcel(jc)} title="Export Excel">
                          <FileSpreadsheet className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => printJobCard(jc)} title="Print" data-testid={`print-btn-${jc.job_card_number}`}>
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setNoteData(prev => ({ ...prev, job_card_id: jc.id }));
                            setShowNoteDialog(true);
                          }}
                        >
                          <MessageSquare className="w-4 h-4 mr-1" />
                          Note
                        </Button>
                        {jc.status === 'pending_supervisor_review' && (
                          <>
                            <Button size="sm" variant="outline" className="border-blue-400 text-blue-700 hover:bg-blue-50" onClick={() => setBillJobCard(jc)} data-testid={`review-bill-${jc.job_card_number}`}>
                              <Receipt className="w-4 h-4 mr-1" /> Review bill
                            </Button>
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleApproveJobCard(jc.id)} data-testid={`approve-jc-${jc.job_card_number}`}>
                              <Check className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50" onClick={() => { setReopenReason(''); setReopenJobCard(jc); }} data-testid={`reopen-jc-${jc.job_card_number}`}>
                              <RotateCcw className="w-4 h-4 mr-1" /> Back to production
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => { setRejectingJobCardId(jc.id); setShowRejectDialog(true); }} data-testid={`reject-jc-${jc.job_card_number}`}>
                              <X className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {filteredJobCards.length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center">
                    <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">
                      {jobCards.length === 0 ? 'No open job cards' : 'No job cards match the current filter'}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Invoices Tab - Upload multiple invoice photos for received BOMs */}
          <TabsContent value="invoices" className="space-y-4" data-testid="invoices-tab-content">
            <h2 className="text-lg font-semibold">Materials & Invoice Upload</h2>
            <p className="text-sm text-gray-500">Upload multiple invoice photos per BOM (materials from different suppliers).</p>

            <div className="grid gap-4">
              {receivedBOMs.length > 0 ? (
                receivedBOMs.map(bom => {
                  // Combine legacy single photo with new multi-photo array
                  const allPhotos = [
                    ...(bom.invoice_photo_url && !bom.invoice_photos?.includes(bom.invoice_photo_url) ? [bom.invoice_photo_url] : []),
                    ...(bom.invoice_photos || [])
                  ];
                  const photoCount = allPhotos.length;
                  
                  return (
                  <Card key={bom.id} data-testid={`invoice-bom-card-${bom.bom_number}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold">{bom.bom_number}</h3>
                            {bom.status === 'received' ? (
                              <Badge className="bg-green-100 text-green-800">Received</Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-800">Awaiting Materials</Badge>
                            )}
                            {photoCount > 0 && (
                              <Badge className="bg-blue-100 text-blue-800">{photoCount} Invoice{photoCount > 1 ? 's' : ''}</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{bom.client_name}</p>
                          <p className="text-xs text-gray-500">{bom.materials?.length || 0} materials</p>
                          {bom.invoice_uploaded_by && (
                            <p className="text-xs text-gray-400 mt-1">
                              Last upload by: {bom.invoice_uploaded_by}
                              {bom.invoice_uploaded_at && ` on ${new Date(bom.invoice_uploaded_at).toLocaleDateString()}`}
                            </p>
                          )}
                        </div>
                        {bom.status === 'received' && (
                          <Button 
                            size="sm" 
                            className="bg-blue-600 hover:bg-blue-700 flex-shrink-0"
                            onClick={() => openUploadDialog(bom)}
                            data-testid={`upload-invoice-${bom.bom_number}`}
                          >
                            <Camera className="w-4 h-4 mr-1" /> Add Invoice
                          </Button>
                        )}
                      </div>
                      
                      {/* Invoice Photos Gallery */}
                      {photoCount > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-2" data-testid={`invoice-gallery-${bom.bom_number}`}>
                          {allPhotos.map((url, idx) => (
                            <div key={idx} className="relative group border rounded-lg overflow-hidden">
                              <img 
                                src={`${API_BASE}${url}`} 
                                alt={`Invoice ${idx + 1}`}
                                className="w-full h-28 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => viewInvoicePhoto(url)}
                                data-testid={`invoice-photo-${bom.bom_number}-${idx}`}
                              />
                              <button
                                onClick={() => handleDeleteInvoicePhoto(bom.id, url)}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`delete-invoice-photo-${bom.bom_number}-${idx}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                              <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] p-1 text-center">
                                Invoice {idx + 1}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {bom.status === 'received' && photoCount === 0 && (
                        <p className="text-xs text-gray-400 mt-2 text-center py-2 bg-gray-50 rounded">No invoices uploaded yet</p>
                      )}
                    </CardContent>
                  </Card>
                  );
                })
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Receipt className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No received materials pending invoice upload</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <PerformanceReports userRole={user?.role} />
          </TabsContent>

          {/* Operators Tab */}
          <TabsContent value="operators" className="space-y-4">
            <h2 className="text-lg font-semibold">Operators</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {operators.map(op => (
                <Card key={op.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium">{op.name}</p>
                          <p className="text-sm text-gray-500">{op.role?.replace(/_/g, ' ')}</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setNoteData(prev => ({ ...prev, operator_id: op.id }));
                          setShowNoteDialog(true);
                        }}
                      >
                        <MessageSquare className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="space-y-4">
            <h2 className="text-lg font-semibold">Today's Activity</h2>
            <div className="space-y-3">
              {activities.length > 0 ? (
                activities.map(activity => (
                  <Card key={activity.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{activity.operator_name}</p>
                          <p className="text-sm text-gray-500">
                            {activity.operation_type} on {activity.job_card_number}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-blue-600">{activity.quantity} units</p>
                          <p className="text-xs text-gray-500">
                            {new Date(activity.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Activity className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No activity logged today</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Internal Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!noteData.job_card_id && !noteData.operator_id && (
              <>
                <div className="space-y-2">
                  <Label>Job Card (Optional)</Label>
                  <Select 
                    value={noteData.job_card_id} 
                    onValueChange={(v) => setNoteData(prev => ({ ...prev, job_card_id: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select job card" />
                    </SelectTrigger>
                    <SelectContent>
                      {jobCards.map(jc => (
                        <SelectItem key={jc.id} value={jc.id}>{jc.job_card_number}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Operator (Optional)</Label>
                  <Select 
                    value={noteData.operator_id} 
                    onValueChange={(v) => setNoteData(prev => ({ ...prev, operator_id: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select operator" />
                    </SelectTrigger>
                    <SelectContent>
                      {operators.map(op => (
                        <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>Note *</Label>
              <Textarea
                value={noteData.note}
                onChange={(e) => setNoteData(prev => ({ ...prev, note: e.target.value }))}
                placeholder="Enter your internal note..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowNoteDialog(false);
              setNoteData({ job_card_id: '', operator_id: '', note: '' });
            }}>Cancel</Button>
            <Button onClick={handleCreateNote} disabled={!noteData.note}>Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => { setShowUploadDialog(open); if (!open) { setPreviewUrl(null); setSelectedBOM(null); } }}>
        <DialogContent className="sm:max-w-md" data-testid="upload-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Add Invoice Photo</DialogTitle>
            <DialogDescription>
              {selectedBOM && `Add invoice for ${selectedBOM.bom_number} - ${selectedBOM.client_name}`}
              {selectedBOM && (selectedBOM.invoice_photos?.length > 0 || selectedBOM.invoice_photo_url) && (
                <span className="block text-blue-600 mt-1">
                  {(selectedBOM.invoice_photos?.length || 0) + (selectedBOM.invoice_photo_url && !selectedBOM.invoice_photos?.includes(selectedBOM.invoice_photo_url) ? 1 : 0)} invoice(s) already uploaded
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              data-testid="invoice-dropzone"
            >
              {previewUrl ? (
                <div className="relative">
                  <img src={previewUrl} alt="Preview" className="max-h-48 mx-auto rounded-lg object-contain" />
                  <p className="text-sm text-gray-500 mt-2">Click to change photo</p>
                </div>
              ) : (
                <>
                  <Camera className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm font-medium text-gray-700">Click to select invoice photo</p>
                  <p className="text-xs text-gray-500 mt-1">JPG, PNG, WEBP up to 10MB</p>
                </>
              )}
              <input 
                ref={fileInputRef}
                type="file" 
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
                data-testid="invoice-file-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowUploadDialog(false); setPreviewUrl(null); }}>Cancel</Button>
            <Button 
              onClick={() => {
                if (fileInputRef.current?.files[0]) {
                  handleUploadInvoice(fileInputRef.current.files[0]);
                }
              }}
              disabled={!previewUrl || uploading}
              data-testid="confirm-upload-invoice"
            >
              {uploading ? 'Uploading...' : 'Add Invoice Photo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Invoice Photo Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="sm:max-w-2xl" data-testid="view-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Invoice Photo</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-2">
            {viewingPhotoUrl && (
              <img 
                src={`${API_BASE}${viewingPhotoUrl}`} 
                alt="Invoice" 
                className="max-h-[60vh] w-auto rounded-lg object-contain"
                data-testid="invoice-photo-image"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPhotoDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Card Detail Dialog - Pressing & Finishing Details */}
      <Dialog open={!!viewJobCard} onOpenChange={() => setViewJobCard(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="supervisor-jc-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {viewJobCard?.job_card_number} — {viewJobCard?.customer_name}
            </DialogTitle>
            <DialogDescription>
              Status: {viewJobCard?.status?.replace(/_/g, ' ')} | Coordinator: {viewJobCard?.created_by_name || '-'}
            </DialogDescription>
          </DialogHeader>
          
          {viewJobCard && (
            <div className="space-y-5 py-2">
              {/* Job Card Info */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm bg-gray-50 p-3 rounded-lg border">
                <div><span className="font-medium text-gray-500">Reference:</span> <span className="text-gray-800">{viewJobCard.reference || '-'}</span></div>
                <div><span className="font-medium text-gray-500">Date:</span> <span className="text-gray-800">{viewJobCard.date || '-'}</span></div>
                <div><span className="font-medium text-gray-500">Mobile:</span> <span className="text-gray-800">{viewJobCard.mobile_no || '-'}</span></div>
                <div><span className="font-medium text-gray-500">Delivery:</span> <span className="text-gray-800">{viewJobCard.delivery_date || '-'}</span></div>
                <div><span className="font-medium text-gray-500">Work In-Charge:</span> <span className="text-gray-800">{viewJobCard.work_in_charge || '-'}</span></div>
              </div>

              {/* Summary Stats */}
              <PressingBalance jobCard={viewJobCard} variant="full" className="pb-3" />

              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-orange-50 rounded-lg text-center">
                  <p className="text-xs text-orange-600">Cutting</p>
                  <p className="text-xl font-bold text-orange-700">{viewJobCard.cutting_qty_total || 0}</p>
                  {viewJobCard.cutting_status && (
                    <p className="text-[10px] mt-1 text-orange-500">{viewJobCard.cutting_status.replace(/_/g, ' ')}</p>
                  )}
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-xs text-blue-600">Pressing</p>
                  {(() => {
                    const p = pressingProgress(viewJobCard);
                    return (
                      <div>
                        <p className="text-xl font-bold text-blue-700">{p.done}/{p.planned}</p>
                        <p className="text-[10px] text-blue-500">{p.balance > 0 ? `${p.balance} left` : 'done'}</p>
                      </div>
                    );
                  })()}
                </div>
                <div className="p-3 bg-purple-50 rounded-lg text-center">
                  <p className="text-xs text-purple-600">Boring</p>
                  <p className="text-xl font-bold text-purple-700">{viewJobCard.boring_qty_total || 0}</p>
                  {viewJobCard.boring_status && (
                    <p className="text-[10px] mt-1 text-purple-500">{viewJobCard.boring_status.replace(/_/g, ' ')}</p>
                  )}
                </div>
              </div>

              {/* Operator Notes */}
              {(viewJobCard.cutting_operator_note || viewJobCard.boring_operator_note) && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 space-y-1">
                  <h4 className="text-xs font-semibold text-teal-700">Operator Notes</h4>
                  {viewJobCard.cutting_operator_note && (
                    <p className="text-xs text-teal-600"><span className="font-medium">Cutting:</span> {viewJobCard.cutting_operator_note}</p>
                  )}
                  {viewJobCard.boring_operator_note && (
                    <p className="text-xs text-teal-600"><span className="font-medium">Boring:</span> {viewJobCard.boring_operator_note}</p>
                  )}
                </div>
              )}

              {/* Normal Pressing Lines */}
              {viewJobCard.normal_pressing_lines?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-blue-700">Normal Pressing Lines</h4>
                  <table className="w-full text-xs border">
                    <thead className="bg-blue-50">
                      <tr>
                        <th className="border p-1.5 text-center w-8">#</th>
                        <th className="border p-1.5 text-left">Description</th>
                        <th className="border p-1.5 text-center w-14">Ply Qty</th>
                        <th className="border p-1.5 text-center w-10">Side</th>
                        <th className="border p-1.5 text-center w-14">Total</th>
                        <th className="border p-1.5 text-left">Details</th>
                        <th className="border p-1.5 text-center w-20">Status</th>
                        <th className="border p-1.5 text-center w-16">Machine</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewJobCard.normal_pressing_lines.map((line, i) => (
                        <tr key={line.id || i} className={line.status === 'completed' ? 'bg-green-50' : ''}>
                          <td className="border p-1.5 text-center">{line.sl_no || i + 1}</td>
                          <td className="border p-1.5">{line.description}</td>
                          <td className="border p-1.5 text-center">{line.ply_qty}</td>
                          <td className="border p-1.5 text-center">{line.side}</td>
                          <td className="border p-1.5 text-center font-medium">{line.total || line.ply_qty * line.side}</td>
                          <td className="border p-1.5 text-gray-600">{line.details || '-'}</td>
                          <td className="border p-1.5 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${line.status === 'completed' ? 'bg-green-100 text-green-700' : line.status === 'started' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                              {line.status?.replace(/_/g, ' ') || 'not started'}
                            </span>
                          </td>
                          <td className="border p-1.5 text-center text-[10px] font-medium">
                            {line.machine_type ? line.machine_type.toUpperCase() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Hot Pressing Lines */}
              {viewJobCard.hot_pressing_lines?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-red-700">Hot Pressing Lines</h4>
                  <table className="w-full text-xs border">
                    <thead className="bg-red-50">
                      <tr>
                        <th className="border p-1.5 text-center w-8">#</th>
                        <th className="border p-1.5 text-left">Description</th>
                        <th className="border p-1.5 text-center w-14">Ply Qty</th>
                        <th className="border p-1.5 text-center w-10">Side</th>
                        <th className="border p-1.5 text-center w-14">Total</th>
                        <th className="border p-1.5 text-left">Details</th>
                        <th className="border p-1.5 text-center w-20">Status</th>
                        <th className="border p-1.5 text-center w-16">Machine</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewJobCard.hot_pressing_lines.map((line, i) => (
                        <tr key={line.id || i} className={line.status === 'completed' ? 'bg-green-50' : ''}>
                          <td className="border p-1.5 text-center">{line.sl_no || i + 1}</td>
                          <td className="border p-1.5">{line.description}</td>
                          <td className="border p-1.5 text-center">{line.ply_qty}</td>
                          <td className="border p-1.5 text-center">{line.side}</td>
                          <td className="border p-1.5 text-center font-medium">{line.total || line.ply_qty * line.side}</td>
                          <td className="border p-1.5 text-gray-600">{line.details || '-'}</td>
                          <td className="border p-1.5 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${line.status === 'completed' ? 'bg-green-100 text-green-700' : line.status === 'started' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                              {line.status?.replace(/_/g, ' ') || 'not started'}
                            </span>
                          </td>
                          <td className="border p-1.5 text-center text-[10px] font-medium">
                            {line.machine_type ? line.machine_type.toUpperCase() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* PVC & Acrylic Pressing Lines */}
              {viewJobCard.veneer_pressing_lines?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-amber-700">PVC & Acrylic Pressing Lines</h4>
                  <table className="w-full text-xs border">
                    <thead className="bg-amber-50">
                      <tr>
                        <th className="border p-1.5 text-center w-8">#</th>
                        <th className="border p-1.5 text-left">Description</th>
                        <th className="border p-1.5 text-center w-14">Ply Qty</th>
                        <th className="border p-1.5 text-center w-10">Side</th>
                        <th className="border p-1.5 text-center w-14">Total</th>
                        <th className="border p-1.5 text-left">Details</th>
                        <th className="border p-1.5 text-center w-20">Status</th>
                        <th className="border p-1.5 text-center w-16">Machine</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewJobCard.veneer_pressing_lines.map((line, i) => (
                        <tr key={line.id || i} className={line.status === 'completed' ? 'bg-green-50' : ''}>
                          <td className="border p-1.5 text-center">{line.sl_no || i + 1}</td>
                          <td className="border p-1.5">{line.description}</td>
                          <td className="border p-1.5 text-center">{line.ply_qty}</td>
                          <td className="border p-1.5 text-center">{line.side}</td>
                          <td className="border p-1.5 text-center font-medium">{line.total || line.ply_qty * line.side}</td>
                          <td className="border p-1.5 text-gray-600">{line.details || '-'}</td>
                          <td className="border p-1.5 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${line.status === 'completed' ? 'bg-green-100 text-green-700' : line.status === 'started' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                              {line.status?.replace(/_/g, ' ') || 'not started'}
                            </span>
                          </td>
                          <td className="border p-1.5 text-center text-[10px] font-medium">
                            {line.machine_type ? line.machine_type.toUpperCase() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pressing Diagrams/Images */}
              {viewJobCard.pressing_images?.length > 0 && (
                <div data-testid="supervisor-pressing-images">
                  <h4 className="text-sm font-semibold mb-2 text-orange-700 flex items-center gap-1">
                    <Image className="w-4 h-4" /> Pressing Diagrams ({viewJobCard.pressing_images.length})
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {viewJobCard.pressing_images.map((url, idx) => (
                      <img 
                        key={idx}
                        src={`${API_BASE}${url}`} 
                        alt={`Pressing diagram ${idx + 1}`}
                        className="w-full h-28 object-cover rounded border cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setViewingPressingImage(url)}
                        data-testid={`supervisor-pressing-image-${idx}`}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Finishing Lines */}
              {viewJobCard.finishing_lines?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-pink-700">Finishing Works</h4>
                  <table className="w-full text-xs border">
                    <thead className="bg-pink-50">
                      <tr>
                        <th className="border p-1.5 text-center w-8">#</th>
                        <th className="border p-1.5 text-left">Description</th>
                        <th className="border p-1.5 text-center w-12">Unit</th>
                        <th className="border p-1.5 text-center w-12">Qty</th>
                        <th className="border p-1.5 text-right w-16">Rate</th>
                        <th className="border p-1.5 text-right w-20">Total</th>
                        <th className="border p-1.5 text-left">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewJobCard.finishing_lines.map((line, i) => (
                        <tr key={line?.id || i}>
                          <td className="border p-1.5 text-center">{i + 1}</td>
                          <td className="border p-1.5">{line.description}</td>
                          <td className="border p-1.5 text-center">{line.unit}</td>
                          <td className="border p-1.5 text-center">{line.qty}</td>
                          <td className="border p-1.5 text-right">{line.rate?.toFixed(2)}</td>
                          <td className="border p-1.5 text-right font-medium">{line.total?.toFixed(2)}</td>
                          <td className="border p-1.5 text-gray-600">{line.details || '-'}</td>
                        </tr>
                      ))}
                      <tr className="font-semibold bg-pink-50">
                        <td className="border p-1.5" colSpan={5} align="right">Total:</td>
                        <td className="border p-1.5 text-right">
                          {viewJobCard.finishing_lines.reduce((sum, l) => sum + (l.total || 0), 0).toFixed(2)}
                        </td>
                        <td className="border p-1.5"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* No pressing or finishing data */}
              {!viewJobCard.normal_pressing_lines?.length && !viewJobCard.hot_pressing_lines?.length && !viewJobCard.veneer_pressing_lines?.length && !viewJobCard.finishing_lines?.length && (
                <div className="text-center py-6 text-gray-400">
                  <FileText className="w-10 h-10 mx-auto mb-2" />
                  <p>No pressing or finishing details added yet</p>
                </div>
              )}

              {/* Coordinator Tasks */}
              {viewJobCard.coordinator_tasks?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">Coordinator Tasks</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {viewJobCard.coordinator_tasks.map((task, i) => (
                      <div key={task?.task_type || i} className={`p-2 rounded-lg border text-center ${task.status === 'completed' ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                        <p className="text-xs font-medium">{task.task_type?.replace(/_/g, ' ')}</p>
                        {task.status === 'completed' ? (
                          <p className="text-[10px] text-green-700 mt-0.5">by {task.performed_by}</p>
                        ) : (
                          <p className="text-[10px] text-gray-400 mt-0.5">Pending</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Bill to approve - the confirmed lines, before an estimate exists */}
              {viewJobCard.status === 'pending_supervisor_review' && (
                <div className="rounded-lg border border-blue-200 p-3">
                  <BillView jobCardId={viewJobCard.id} title="Bill to approve" />
                </div>
              )}

              {/* Estimate/Billing Details */}
              {(() => {
                const est = estimates.find(e => e.job_card_id === viewJobCard.id || e.project_id === viewJobCard.project_id);
                if (!est || !est.estimate_lines?.length) return null;
                return (
                  <div data-testid="supervisor-estimate-section">
                    <h4 className="text-sm font-semibold mb-2 text-green-700">Estimate ({est.estimate_number})</h4>
                    <table className="w-full text-xs border">
                      <thead className="bg-green-50">
                        <tr>
                          <th className="border p-1.5 text-center w-8">#</th>
                          <th className="border p-1.5 text-left">Particular</th>
                          <th className="border p-1.5 text-center w-12">Qty</th>
                          <th className="border p-1.5 text-center w-12">Unit</th>
                          <th className="border p-1.5 text-right w-16">Rate</th>
                          <th className="border p-1.5 text-right w-20">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {est.estimate_lines.map((line, i) => (
                          <tr key={`est-${i}`}>
                            <td className="border p-1.5 text-center">{i + 1}</td>
                            <td className="border p-1.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span>{line.particular}</span>
                                {line.breakdown && line.breakdown.length > 0 && (
                                  <button
                                    type="button"
                                    className="text-[11px] text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                                    onClick={() => setBreakdownLine(line)}
                                    data-testid={`sup-view-breakdown-${i}`}
                                  >
                                    View breakdown ({line.breakdown.length})
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="border p-1.5 text-center">{line.qty}</td>
                            <td className="border p-1.5 text-center">{line.unit || ''}</td>
                            <td className="border p-1.5 text-right">₹{(line.rate || 0).toFixed(2)}</td>
                            <td className="border p-1.5 text-right font-medium">₹{(line.total || line.amount || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr className="font-semibold bg-green-50">
                          <td className="border p-1.5" colSpan={5} align="right">Total:</td>
                          <td className="border p-1.5 text-right">₹{(est.total_amount || 0).toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          )}
          
          {viewJobCard && (
            <div className="mt-4 space-y-3">
              <WorkLogHistory jobCardId={viewJobCard.id} />
              <OperatorEditHistory jobCardId={viewJobCard.id} />
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => handleExportPDF(viewJobCard)}>
              <Download className="w-4 h-4 mr-1" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExportExcel(viewJobCard)}>
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => printJobCard(viewJobCard)} data-testid="print-view-btn">
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
            <Button variant="outline" onClick={() => setViewJobCard(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Full Pressing Image Dialog */}
      <Dialog open={!!viewingPressingImage} onOpenChange={() => setViewingPressingImage(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="supervisor-view-pressing-image-dialog">
          <DialogHeader>
            <DialogTitle>Pressing Diagram</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-2">
            {viewingPressingImage && (
              <img 
                src={`${API_BASE}${viewingPressingImage}`} 
                alt="Pressing diagram" 
                className="max-h-[60vh] w-auto rounded-lg object-contain"
                data-testid="supervisor-pressing-image-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Review bill dialog - bill with the approval actions underneath */}
      <BillDialog
        jobCard={billJobCard}
        title="Bill to approve"
        onClose={() => setBillJobCard(null)}
        footer={billJobCard && (
          <>
            <Button variant="outline" onClick={() => setBillJobCard(null)}>Close</Button>
            <Button
              variant="outline"
              className="border-amber-400 text-amber-700 hover:bg-amber-50"
              onClick={() => { const jc = billJobCard; setBillJobCard(null); setReopenReason(''); setReopenJobCard(jc); }}
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Back to production
            </Button>
            <Button
              variant="destructive"
              onClick={() => { const id = billJobCard.id; setBillJobCard(null); setRejectingJobCardId(id); setShowRejectDialog(true); }}
            >
              <X className="w-4 h-4 mr-1" /> Reject
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={async () => { const id = billJobCard.id; setBillJobCard(null); await handleApproveJobCard(id); }}
              data-testid="bill-approve-btn"
            >
              <Check className="w-4 h-4 mr-1" /> Approve &amp; send to accounts
            </Button>
          </>
        )}
      />

      {/* Back to production dialog */}
      <Dialog open={!!reopenJobCard} onOpenChange={(open) => { if (!open) closeReopenDialog(); }}>
        <DialogContent data-testid="reopen-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <RotateCcw className="w-5 h-5" /> Send JC {reopenJobCard?.job_card_number} back to production
            </DialogTitle>
            <DialogDescription>
              The card goes back on the operators' list so the remaining work can be finished.
              {reopenJobCard?.created_by_name ? ` ${reopenJobCard.created_by_name} will be notified and can send it for review again once it's done.` : ''}
            </DialogDescription>
          </DialogHeader>
          {reopenJobCard && pressingProgress(reopenJobCard).balance > 0 && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Pressing has {pressingProgress(reopenJobCard).balance} of {pressingProgress(reopenJobCard).planned} still to press.
            </p>
          )}
          <div className="space-y-2">
            <Label htmlFor="reopen-reason">Reason <span className="text-red-500">*</span></Label>
            <Textarea
              id="reopen-reason"
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              placeholder="e.g. 24 sheets moved from acrylic to normal pressing"
              className="min-h-[80px]"
              data-testid="reopen-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeReopenDialog}>Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleReopenForProduction}
              disabled={reopening || !reopenReason.trim()}
              data-testid="confirm-reopen-btn"
            >
              {reopening ? 'Sending...' : 'Send back to production'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Job Card</DialogTitle>
            <DialogDescription>Provide a reason for rejection. This will be sent back to the coordinator.</DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter rejection reason..."
            className="min-h-[80px]"
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectDialog(false); setRejectReason(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleRejectJobCard} data-testid="confirm-reject-btn">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Dialog - Shows filtered job cards */}
      <Dialog open={showStatsDialog} onOpenChange={setShowStatsDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="sup-stats-dialog">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${getStatsDialogColor()}`}>
              {statsDialogType === 'active' && <FolderKanban className="w-5 h-5" />}
              {statsDialogType === 'pending_review' && <AlertCircle className="w-5 h-5" />}
              {statsDialogType === 'completed' && <CheckCircle className="w-5 h-5" />}
              {getStatsDialogTitle()}
              <Badge variant="secondary" className="ml-2">{getStatsJobCards().length}</Badge>
            </DialogTitle>
            <DialogDescription>
              {statsDialogType === 'active' && 'Job cards currently in production'}
              {statsDialogType === 'pending_review' && 'Job cards awaiting your review and approval'}
              {statsDialogType === 'completed' && 'Job cards that have been completed'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 mt-4">
            {getStatsJobCards().length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No job cards in this category</p>
              </div>
            ) : (
              getStatsJobCards().map(jc => (
                <div
                  key={jc.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-blue-50 hover:shadow-sm transition-all border"
                  onClick={() => handleStatsJobCardClick(jc)}
                  data-testid={`sup-stats-jc-${jc.job_card_number}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <p className="font-semibold text-blue-700 underline decoration-dotted text-lg">
                        #{jc.job_card_number}
                      </p>
                      <Badge className={
                        jc.status === 'in_production' ? 'bg-emerald-500 text-white' :
                        jc.status === 'pending_supervisor_review' ? 'bg-orange-500 text-white' :
                        jc.status === 'completed' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }>
                        {jc.status?.replace(/_/g, ' ')}
                      </Badge>
                      {isDelayed(jc) && (
                        <Badge className="bg-red-100 text-red-700 border border-red-300 animate-pulse">
                          Delayed
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{jc.customer_name || 'Unknown Client'}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      {jc.created_by_name && (
                        <p className="text-xs text-blue-600">Coordinator: {jc.created_by_name}</p>
                      )}
                      {jc.created_at && (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Created: {new Date(jc.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div className="text-center">
                        <p className="font-bold text-blue-600">{jc.cutting_qty_total || 0}</p>
                        <p className="text-gray-500">Cutting</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-amber-600">{jc.pressing_qty_total || 0}</p>
                        <p className="text-gray-500">Pressing</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-purple-600">{jc.boring_qty_total || 0}</p>
                        <p className="text-gray-500">Boring</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowStatsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Estimate Line Breakdown sub-dialog (for collated pressing lines) */}
      <Dialog open={!!breakdownLine} onOpenChange={() => setBreakdownLine(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="sup-breakdown-dialog">
          <DialogHeader>
            <DialogTitle>Breakdown — {breakdownLine?.particular}</DialogTitle>
            <DialogDescription>
              Original detail lines from the job card that were collated into this single estimate row.
            </DialogDescription>
          </DialogHeader>
          {breakdownLine && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="border p-2 text-left">#</th>
                    <th className="border p-2 text-left">Description</th>
                    <th className="border p-2 text-center w-16">Ply</th>
                    <th className="border p-2 text-center w-16">Side</th>
                    <th className="border p-2 text-center w-20">Qty</th>
                    <th className="border p-2 text-center w-20">Rate</th>
                    <th className="border p-2 text-right w-24">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(breakdownLine.breakdown || []).map((b, i) => (
                    <tr key={i} data-testid={`sup-breakdown-row-${i}`}>
                      <td className="border p-2 text-center">{i + 1}</td>
                      <td className="border p-2">{b.description}</td>
                      <td className="border p-2 text-center">{b.ply_qty}</td>
                      <td className="border p-2 text-center">{b.side}</td>
                      <td className="border p-2 text-center">{b.qty}</td>
                      <td className="border p-2 text-center">{Number(b.rate).toFixed(2)}</td>
                      <td className="border p-2 text-right font-medium">{Number(b.amount).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-bold">
                    <td className="border p-2 text-right" colSpan="4">Total</td>
                    <td className="border p-2 text-center">{breakdownLine.qty}</td>
                    <td className="border p-2"></td>
                    <td className="border p-2 text-right text-blue-700">{Number(breakdownLine.amount ?? breakdownLine.total ?? (breakdownLine.qty * breakdownLine.rate)).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupervisorDashboard;
